import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react'
import axios from "axios";
import MedioCardGrid from "../MedioCardGrid.jsx";
import MedioCardList from "../MedioCardList.jsx";
import MedioCardSkeleton from "../MedioCardSkeleton.jsx";
import FiltrosExplorador from "./FiltrosExplorador.jsx";
import SubirMedioModal from "./SubirMedioModal.jsx";
import "/src/pages/Profile/Profile.css"
import Cookies from "js-cookie";
import classNames from "classnames";
import DropdownOrder from "../../../../Store/DropdownOrder.jsx";
import DropdownTipoVista from "../../../../Store/DropdownTipoVista.jsx";
import BuscadorInput from "../../../../Store/BuscadorInput.jsx";
import { Calendar, Camera, RefreshCw, Upload } from "lucide-react";

// — Hook de debounce para la búsqueda —
const useDebounce = (value, delay) => {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const handler = setTimeout(() => setDebounced(value), delay);
        return () => clearTimeout(handler);
    }, [value, delay]);
    return debounced;
};

const ExploradorMedios = ({ onMedioSelect, showFilters = false }) => {

    // — Estados de datos y paginación —
    const [mediaList, setMediaList] = useState([]);
    const [loading, setLoading]   = useState(false);
    const [error, setError]       = useState(null);
    const [hasMore, setHasMore]   = useState(true);
    const pageRef                 = useRef(1);

    // — Estados de filtros/vista —
    const initialView = {
        mode: "grid",
        columns: 3,
        sortBy: "created_at",
        sortDirection: "desc",
    };
    const [viewMode, setViewMode] = useState(() => {
        const saved = Cookies.get("viewModeMediosExplorer");
        return saved ? JSON.parse(saved) : initialView;
    });
    const [searchTerm, setSearchTerm] = useState("");
    const [sharingFilter, setSharingFilter] = useState("todos");
    const [showUpload, setShowUpload] = useState(false);
    const debouncedSearchTerm = useDebounce(searchTerm, 300);

    const arrayDatosOrdenacion = [
        { nombre: "Nombre",
            valor: "nombre",
            icon: <Camera className="w-5 h-5 mr-2" />
        },
        { nombre: "Fecha de Creación",
            valor: "created_at",
            icon: <Calendar className="mr-2 h-5 w-5" aria-hidden="true" />
        },
        { nombre: "Fecha de Modificación",
            valor: "updated_at",
            icon: <Calendar className="mr-2 h-5 w-5" aria-hidden="true" />
        }
    ];

    // — Función de fetch paginada —
    // reset=true reemplaza la lista (cambio de filtros / búsqueda);
    // reset=false concatena (scroll infinito).
    const fetchMedios = useCallback(async (pageNum, reset = false) => {
        setLoading(true);
        setError(null);
        try {
            const { data } = await axios.get("/media/", {
                params: {
                    page: pageNum,
                    sortBy: viewMode.sortBy,
                    sortDirection: viewMode.sortDirection,
                    search: debouncedSearchTerm || undefined,
                    sharing_permissions: sharingFilter !== 'todos' ? sharingFilter : undefined,
                },
            });
            // backend devuelve { results, next, previous, count }
            setMediaList(prev =>
                reset || pageNum === 1 ? data.results : [...prev, ...data.results]
            );
            setHasMore(Boolean(data.next));
        } catch (err) {
            console.error("Error fetching medios:", err);
            setError("Error al cargar los medios");
        } finally {
            setLoading(false);
        }
    }, [viewMode.sortBy, viewMode.sortDirection, debouncedSearchTerm, sharingFilter]);

    // — Resetea a página 1 y recarga cuando cambian filtros / orden / búsqueda —
    useEffect(() => {
        pageRef.current = 1;
        fetchMedios(1, true);
    }, [fetchMedios]);

    // — Persiste la vista elegida —
    useEffect(() => {
        Cookies.set("viewModeMediosExplorer", JSON.stringify(viewMode), { expires: 7 });
    }, [viewMode]);

    // — Observer para detectar el último elemento y cargar la siguiente página —
    const observer = useRef();
    const lastItemRef = useCallback(node => {
        if (loading) return;
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore && !loading) {
                const nextPage = pageRef.current + 1;
                pageRef.current = nextPage;
                fetchMedios(nextPage, false);
            }
        }, { threshold: 0.1 });
        if (node) observer.current.observe(node);
    }, [loading, hasMore, fetchMedios]);

    // — Estilos del grid (inline para que Tailwind no purgue las columnas dinámicas) —
    const gridClassName = useMemo(
        () => viewMode.mode === "grid" ? "grid gap-4" : "flex flex-col space-y-4",
        [viewMode.mode]
    );
    const gridStyle = useMemo(
        () => viewMode.mode === "grid"
            ? { gridTemplateColumns: `repeat(${viewMode.columns}, minmax(0, 1fr))` }
            : {},
        [viewMode.mode, viewMode.columns]
    );

    // — Estados de UI —
    const showSkeletons  = loading && mediaList.length === 0;
    const showEmptyState = !loading && !error && mediaList.length === 0;

    const handleRetry = useCallback(() => {
        pageRef.current = 1;
        fetchMedios(1, true);
    }, [fetchMedios]);

    // Tras subir un medio, recarga desde la primera página para que aparezca arriba
    const handleMedioCreated = useCallback(() => {
        pageRef.current = 1;
        fetchMedios(1, true);
    }, [fetchMedios]);

    return (
        <div className="min-h-full max-h-full flex flex-col gap-3">
            <div className="glassCard p-3 flex flex-col gap-2 relative z-40">
                {/* Fila 1: filtros de visibilidad */}
                <FiltrosExplorador value={sharingFilter} onChange={setSharingFilter} />

                {/* Fila 2: subir (izquierda) + orden, vista y búsqueda (derecha) */}
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                    <button
                        type="button"
                        onClick={() => setShowUpload(true)}
                        className="btn btn-primary btn-sm gap-1.5 w-full sm:w-auto sm:mr-auto"
                    >
                        <Upload className="w-4 h-4" />
                        Subir medio
                    </button>
                    <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-row sm:items-center sm:justify-end">
                        <DropdownOrder
                            viewMode={viewMode}
                            setViewMode={setViewMode}
                            arrayDatosOrdenacion={arrayDatosOrdenacion}
                            className="w-full sm:w-auto"
                        />
                        <DropdownTipoVista
                            options={[
                                { label: '2 Columnas', value: { mode: 'grid', columns: 2 } },
                                { label: '3 Columnas', value: { mode: 'grid', columns: 3 } },
                                { label: '4 Columnas', value: { mode: 'grid', columns: 4 } },
                                { label: 'Lista',      value: { mode: 'list' } },
                            ]}
                            selected={viewMode.mode}
                            selectedValue={viewMode}
                            onChange={(mode) =>
                                setViewMode((prev) => ({ ...prev, mode: mode.mode, columns: mode.columns }))
                            }
                            className="w-full sm:w-auto"
                        />
                        <div className="col-span-2 sm:col-span-1">
                            <BuscadorInput
                                searchTerm={searchTerm}
                                setSearchTerm={setSearchTerm}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div
                className={classNames(
                    "h-full flex-1 overflow-y-auto scrollbar_mod glassCard p-3 relative z-0",
                    (showEmptyState || error) && "flex items-center justify-center"
                )}
            >
                {/* Estado de error */}
                {error ? (
                    <div className="text-center py-8">
                        <div className="w-16 h-16 mx-auto mb-4 bg-error/10 rounded-full flex items-center justify-center">
                            <RefreshCw className="w-8 h-8 text-error" />
                        </div>
                        <p className="text-base-content/60 mb-4">{error}</p>
                        <button onClick={handleRetry} className="btn btn-outline">
                            <RefreshCw className="w-4 h-4 mr-2" />
                            Reintentar
                        </button>
                    </div>
                ) : showEmptyState ? (
                    <div className="h-full flex items-center justify-center py-12">
                        <div className="text-center">
                            <p className="text-lg text-base-content/50">No hay medios disponibles.</p>
                            <p className="text-sm text-base-content/40 mt-1 mb-4">
                                Sube tu primer medio para empezar.
                            </p>
                            <button
                                type="button"
                                onClick={() => setShowUpload(true)}
                                className="btn btn-primary btn-sm gap-1.5"
                            >
                                <Upload className="w-4 h-4" />
                                Subir medio
                            </button>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className={gridClassName} style={gridStyle}>
                            {/* Skeletons durante la carga inicial */}
                            {showSkeletons &&
                                Array.from({ length: viewMode.columns * 2 }).map((_, i) => (
                                    <MedioCardSkeleton key={`skeleton-${i}`} />
                                ))
                            }

                            {/* Medios reales — el último lleva el ref del observer.
                                Reutiliza las tarjetas del perfil en modo "insertar". */}
                            {mediaList.map((media, idx) => {
                                const isLast = idx === mediaList.length - 1;
                                const Card = viewMode.mode === 'grid' ? MedioCardGrid : MedioCardList;
                                return (
                                    <div key={media.id} ref={isLast ? lastItemRef : undefined}>
                                        <Card media={media} onMedioSelect={onMedioSelect} />
                                    </div>
                                );
                            })}
                        </div>

                        {/* Indicador de carga al final (scroll infinito) */}
                        {loading && mediaList.length > 0 && (
                            <div className="flex justify-center py-4">
                                <div className="flex items-center space-x-2 text-base-content/60">
                                    <div className="loading loading-spinner loading-sm"></div>
                                    <span className="text-sm">Cargando más medios...</span>
                                </div>
                            </div>
                        )}

                        {/* Fin de la lista */}
                        {!hasMore && mediaList.length > 0 && !loading && (
                            <div className="text-center py-6 text-base-content/60">
                                <p className="text-sm">Has visto todos los medios disponibles</p>
                            </div>
                        )}
                    </>
                )}
            </div>

            {showUpload && (
                <SubirMedioModal
                    onClose={() => setShowUpload(false)}
                    onCreated={handleMedioCreated}
                />
            )}
        </div>
    )
}
export default ExploradorMedios
